/**
 * Parsing + prévisualisation d'un CSV de prospects pour le Power Dialer.
 *
 * Miroir du mapping de colonnes et de la normalisation du backend
 * (telephony/views_power_dialer.py : campaign_import_csv / _normalize_phone),
 * pour que la preview reflète exactement ce qui sera importé (numéro requis,
 * lignes sans numéro / invalides / doublons ignorées).
 */

/** Découpe une ligne CSV en gérant les guillemets et les séparateurs , ; tab. */
function parseCSVLine(line: string): string[] {
  const out: string[] = []
  let current = ''
  let inQuotes = false
  for (const ch of line) {
    if (ch === '"') inQuotes = !inQuotes
    else if ((ch === ',' || ch === ';' || ch === '\t') && !inQuotes) {
      out.push(current.trim())
      current = ''
    } else current += ch
  }
  out.push(current.trim())
  return out
}

/** Normalise un numéro en E.164 (NANP par défaut). Retourne null si invalide. */
export function normalizeProspectPhone(raw: string): string | null {
  const cleaned = raw.replace(/["'\s\-(). ]/g, '')
  if (/^\+[1-9]\d{9,14}$/.test(cleaned)) return cleaned
  if (/^1\d{10}$/.test(cleaned)) return `+${cleaned}`
  if (/^\d{10}$/.test(cleaned)) return `+1${cleaned}`
  return null
}

/** Alias d'en-têtes → champ interne (aligné sur COLUMN_MAP du backend). */
const COLUMN_MAP: Record<string, keyof ProspectRow> = {
  phone: 'phone', telephone: 'phone', 'téléphone': 'phone',
  numero: 'phone', 'numéro': 'phone', phone_number: 'phone', tel: 'phone',
  first_name: 'first_name', prenom: 'first_name', 'prénom': 'first_name',
  firstname: 'first_name',
  last_name: 'last_name', nom: 'last_name', lastname: 'last_name',
  company: 'company_name', entreprise: 'company_name', company_name: 'company_name',
  position: 'position', poste: 'position', job_title: 'position', titre: 'position',
  email: 'email', courriel: 'email', 'e-mail': 'email',
}

export interface ProspectRow {
  phone: string
  first_name: string
  last_name: string
  company_name: string
  position: string
  email: string
}

export interface ProspectsCsvResult {
  /** Prospects valides et dédupliqués (numéro normalisé). */
  prospects: ProspectRow[]
  /** Nombre total de lignes de données (hors en-tête). */
  totalRows: number
  /** Lignes ignorées faute de numéro / numéro invalide. */
  skippedNoPhone: number
  /** Lignes ignorées car numéro en double dans le fichier. */
  duplicates: number
  /** True si une colonne téléphone a été détectée. */
  phoneColumnFound: boolean
}

/** Nom affiché d'un prospect (fallback sur le numéro). */
export function prospectDisplayName(p: ProspectRow): string {
  const name = `${p.first_name} ${p.last_name}`.trim()
  return name || p.company_name || p.phone
}

/**
 * Parse un fichier CSV de prospects. Ne lève jamais : renvoie un résultat
 * vide/partiel exploitable pour la preview.
 */
export async function parseProspectsCsv(
  file: File,
): Promise<ProspectsCsvResult> {
  const empty: ProspectsCsvResult = {
    prospects: [], totalRows: 0, skippedNoPhone: 0, duplicates: 0,
    phoneColumnFound: false,
  }

  let text = await file.text()
  // Retire le BOM UTF-8 (Excel).
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1)
  const lines = text.split(/\r?\n|\r/).filter((l) => l.trim().length > 0)
  if (lines.length < 1) return empty

  const headers = parseCSVLine(lines[0]).map((h) => h.trim().toLowerCase())
  const fieldByIndex = headers.map((h) => COLUMN_MAP[h])
  let phoneIdx = fieldByIndex.findIndex((f) => f === 'phone')

  // Fallback : aucune colonne "téléphone" nommée → détection par contenu sur
  // la 1re ligne de données (colonne qui normalise en E.164).
  if (phoneIdx === -1 && lines.length > 1) {
    const firstData = parseCSVLine(lines[1])
    phoneIdx = firstData.findIndex((cell) => normalizeProspectPhone(cell) !== null)
  }
  if (phoneIdx === -1) return { ...empty, totalRows: lines.length - 1 }

  const seen = new Set<string>()
  const prospects: ProspectRow[] = []
  let skippedNoPhone = 0
  let duplicates = 0

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i])
    const phone = normalizeProspectPhone(cells[phoneIdx] ?? '')
    if (!phone) {
      skippedNoPhone++
      continue
    }
    if (seen.has(phone)) {
      duplicates++
      continue
    }
    seen.add(phone)

    const row: ProspectRow = {
      phone,
      first_name: '', last_name: '', company_name: '', position: '', email: '',
    }
    headers.forEach((_, idx) => {
      const field = fieldByIndex[idx]
      if (field && field !== 'phone') {
        row[field] = (cells[idx] ?? '').trim()
      }
    })
    prospects.push(row)
  }

  return {
    prospects,
    totalRows: lines.length - 1,
    skippedNoPhone,
    duplicates,
    phoneColumnFound: true,
  }
}

// ── Modèle CSV téléchargeable ────────────────────────────────────────────
// En-têtes = alias reconnus par le backend ; 2 lignes d'exemple à remplacer.
const TEMPLATE_ROWS = [
  'telephone,prenom,nom,entreprise,poste,email',
  '+15145551234,Marie,Tremblay,Boulangerie Soleil,Gérante,marie@exemple.com',
  '4385559876,Jean,Nadeau,Garage Nadeau,Propriétaire,jean@exemple.com',
]

/** Déclenche le téléchargement d'un modèle CSV valide (prêt à remplir). */
export function downloadProspectsTemplate(
  filename = 'modele-prospects-callizy.csv',
): void {
  // BOM UTF-8 pour qu'Excel affiche correctement les accents.
  const content = '﻿' + TEMPLATE_ROWS.join('\r\n') + '\r\n'
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
